import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, IconButton } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white',
    padding: 'var(--padding-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--gap-sm)'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  chevron: {
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-neutral-700)' }
  },
  compact: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, auto))',
    gap: '4px 18px',
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-700)'
  },
  compactRow: {
    display: 'flex',
    gap: '6px',
    minWidth: 0
  },
  compactLabel: {
    color: 'var(--color-neutral-500)',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  compactValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  expanded: {
    display: 'grid',
    gridTemplateColumns: '160px 1fr',
    gap: '6px',
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-800)',
    marginTop: '8px'
  },
  label: {
    color: 'var(--color-neutral-500)',
    fontWeight: 600
  },
  pre: {
    whiteSpace: 'pre-wrap',
    fontFamily: 'inherit',
    margin: 0,
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-800)'
  },
  jsonBlock: {
    backgroundColor: 'var(--color-neutral-050)',
    padding: '8px',
    borderRadius: 'var(--border-radius-sm)',
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '12px',
    overflow: 'auto',
    maxHeight: '320px',
    whiteSpace: 'pre-wrap'
  }
}))

function CompactRow ({ label, value, classes }) {
  return (
    <div className={classes.compactRow}>
      <span className={classes.compactLabel}>{label}</span>
      <span className={classes.compactValue}>{value}</span>
    </div>
  )
}

CompactRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired
}

function RunConfigCard ({ run }) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)

  const ctx = run.context || {}
  const hasFocus = !!ctx.focus
  const hasTemp = ctx.temperatureOverride !== undefined && ctx.temperatureOverride !== null
  const hasWeights = run.scoreWeights && Object.keys(run.scoreWeights).length > 0

  return (
    <div className={classes.card}>
      <div className={classes.titleRow}>
        <span className={classes.title}>Configuration</span>
        <IconButton
          size='small'
          className={classes.chevron}
          onClick={() => setOpen(prev => !prev)}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>

      <div className={classes.compact}>
        <CompactRow label='Samples' value={run.sampleCount} classes={classes} />
        <CompactRow label='Judge' value={run.judgeModelId || '(task default)'} classes={classes} />
        {ctx.skillId && (
          <CompactRow
            label='Skill'
            value={`${ctx.skillId}${ctx.skillTag ? ` :${ctx.skillTag}` : ''}`}
            classes={classes}
          />
        )}
        {ctx.modelOverride && (
          <CompactRow label='Asst model' value={ctx.modelOverride} classes={classes} />
        )}
        {hasTemp && (
          <CompactRow label='Temp' value={ctx.temperatureOverride} classes={classes} />
        )}
        {hasFocus && (
          <CompactRow label='Focus' value={ctx.focus.subjectLabel} classes={classes} />
        )}
      </div>

      {open && (
        <div className={classes.expanded}>
          <span className={classes.label}>Prompt</span>
          <pre className={classes.pre}>{run.prompt}</pre>
          <span className={classes.label}>Rubric</span>
          <pre className={classes.pre}>{run.rubric}</pre>
          {hasWeights && (
            <>
              <span className={classes.label}>Score weights</span>
              <pre className={classes.jsonBlock}>{JSON.stringify(run.scoreWeights, null, 2)}</pre>
            </>
          )}
          {run.judgeSchema && (
            <>
              <span className={classes.label}>Judge schema</span>
              <pre className={classes.jsonBlock}>{JSON.stringify(run.judgeSchema, null, 2)}</pre>
            </>
          )}
        </div>
      )}
    </div>
  )
}

RunConfigCard.propTypes = {
  run: PropTypes.object.isRequired
}

export default RunConfigCard
